// 聽牌偵測
// 對應 PRD §8.2 聽牌提示、§16.1 效能風險（目標 <50ms）
//
// 「聽牌」＝目前手牌差一張就能胡，列出所有「能補完成胡」的牌種。
//
// 演算法：暴力搜尋。
//   對 34 種可參與胡牌結構的牌種，每種都加進手牌跑一次 canHu()。
//   能胡的就是聽牌。
//
// 效能討論：
// - 34 次 canHu 呼叫
// - canHu 內的回溯演算法 worst case 是指數，但在實際合理手牌下 < 1ms
// - 整個 findTenpaiTiles 預期 < 10ms（M2 機）；極端結構或多刻多牌時可能升到 30–40ms
// - PRD 風險目標 < 50ms，已留充裕餘量
//
// 為什麼用「枚舉 34 種」而不是分析手牌結構推聽牌？
// 暴力法簡單、正確性高、容易驗證；性能已達標。
// 之後若聽牌計算成為 hot path（例：每幀都要算），再考慮做 reverse-pattern 推導優化。

import { addToConcealed, type THand } from './hand';
import { canHu } from './hu';
import type { TTile } from './tile';
import type { TSuit } from './types';
import { Suit } from '@core/types';

/**
 * 34 種可參與胡牌結構的牌種（不含花）。
 * 順序：萬 1-9、筒 1-9、條 1-9、東南西北、中發白。
 *
 * 用 readonly 與 satisfies 雙重保護：
 * - readonly 防意外 push/splice
 * - satisfies 確保陣列每個物件型別正確
 */
const ALL_HU_KINDS: ReadonlyArray<{ readonly suit: TSuit; readonly rank: number }> = [
  ...[1, 2, 3, 4, 5, 6, 7, 8, 9].map((rank) => ({ suit: Suit.Man as TSuit, rank })),
  ...[1, 2, 3, 4, 5, 6, 7, 8, 9].map((rank) => ({ suit: Suit.Pin as TSuit, rank })),
  ...[1, 2, 3, 4, 5, 6, 7, 8, 9].map((rank) => ({ suit: Suit.Sou as TSuit, rank })),
  ...[1, 2, 3, 4].map((rank) => ({ suit: 'wind' as TSuit, rank })),
  ...[1, 2, 3].map((rank) => ({ suit: Suit.Dragon as TSuit, rank })),
];

/** 數出某牌種在手牌（暗牌 + 副露）中已持有的張數。 */
const countOwnedKind = (hand: THand, suit: TSuit, rank: number): number => {
  let n = 0;
  for (const t of hand.concealed) if (t.suit === suit && t.rank === rank) n++;
  for (const m of hand.melds) for (const t of m.tiles) if (t.suit === suit && t.rank === rank) n++;
  return n;
};

/**
 * 列出此手牌的所有聽牌（能讓手牌成立胡牌的進牌種類）。
 *
 * 回傳 TTile 物件陣列，每張用負 id 標記為「假想牌」（id 從 -1 開始遞減），
 * 呼叫端不該用 id 比對，應該用 suit + rank。
 *
 * 前提：手牌狀態必須是「剛出完牌」— concealed 張數 = (5 - melds.length) * 3 + 1。
 * 若不是這個狀態回傳空陣列（例：剛抓完牌時暗牌數量 +1，不是聽牌偵測時機）。
 */
export const findTenpaiTiles = (hand: THand): TTile[] => {
  const setsNeeded = 5 - hand.melds.length;
  // 聽牌時的張數：差 1 張就胡 → setsNeeded * 3 + 1
  if (hand.concealed.length !== setsNeeded * 3 + 1) return [];

  const result: TTile[] = [];
  for (let i = 0; i < ALL_HU_KINDS.length; i++) {
    const kind = ALL_HU_KINDS[i];
    if (!kind) continue;
    // 一副牌每種僅 4 張：自己手牌（含副露）已用滿 4 張的牌種，
    // 牌山不可能再有第 5 張，列為聽張只會誤報絕張，直接略過。
    if (countOwnedKind(hand, kind.suit, kind.rank) >= 4) continue;
    const candidate: TTile = { id: -1 - i, suit: kind.suit, rank: kind.rank };
    const trialHand = addToConcealed(hand, candidate);
    if (canHu(trialHand, candidate, true)) {
      result.push(candidate);
    }
  }
  return result;
};

/** 是否聽牌（任意一張可胡即為 true）。 */
export const isTenpai = (hand: THand): boolean => findTenpaiTiles(hand).length > 0;
